import { mutationProcedure } from '../trpc/common.js';
import type { ApiParams } from '../types/ApiParams.js';
import { ZodLinkPathInput, ZodLinkPathOutput, type LinkPathInput, type LinkPathOutput } from '../types/LinkPath.js';
import { notifyReload } from '../utilities/notifyReload.js';

/**
 * Links a path to the session.
 */
export async function linkPath(
  params: ApiParams<
    LinkPathInput,
    'bus' | 'reporter' | 'telemetryClient' | 'packages' | 'packageHashes',
    'addLinkedPath' | 'incrementSessionVersion' | 'linkedPaths' | 'projectName' | 'resolveMap' | 'sequence'
  >,
): Promise<LinkPathOutput> {
  const { input, ctx } = params;
  const { linkedPath } = input;
  const { session, reporter, bus, telemetryClient, packages, packageHashes } = ctx;

  let result = { resolveMap: session.resolveMap, linkedPaths: session.linkedPaths };
  const task = reporter.addTask(`Linking paths in ${session.projectName}`);
  try {
    await telemetryClient.tracer.startActiveSpan('LINK_PATH', async () => {
      if (linkedPath.remote) {
        packageHashes.extend(linkedPath.remote.packageHashes);
        packages.extend(linkedPath.remote.packages);
      }

      // Add linked paths to session
      result = await session.addLinkedPath(linkedPath);
      task.complete({ status: 'complete', message: `Linked paths`, forceShow: true });
      session.incrementSessionVersion();
      await notifyReload({ reason: 'link-path' }, { bus, session, telemetryClient });
    });
  } catch (e) {
    task.complete({ status: 'fail', message: `Failed to link paths: ${(e as Error).stack || e}`, forceShow: true });
  }
  return result;
}

linkPath.procedure = mutationProcedure<LinkPathInput, LinkPathOutput>(linkPath, ZodLinkPathInput, ZodLinkPathOutput);
